import _get from 'lodash/get'
import {
  post as apiPost,
  TYPE_GRAPHQL,
} from '../../api-client/index'
import { get as getConfig } from '../../config/index'
import { createAuthForRequest } from '../auth/index'
import { log as logError } from '../../log/error'

export const PATH_INFO_QUERY = `
  query PathInfo($path: String!, $filterSet: String, $language: [String]) {
    pathInfo(path: $path, filterSet: $filterSet, language: $language) {
      success
      type
      id
      vocabularyId
      vocabulary
      contentType
      filterSet
      filter
      redirectType
      url
      absolute
      path
      meta {
        title
        description
        keywords
        canonicalUrl
        robotsNoIndex
        robotsNoFollow
        ogTitle
        ogDescription
        ogImage
        ogImageWidth
        ogImageHeight
        ogType
        ogUrl
        twitterTitle
        twitterDescription
        twitterImage
      }
    }
  }
`

interface PathInfoGraphqlPayload {
  success?: boolean
  type?: string
  id?: number
  vocabularyId?: number
  vocabulary?: string
  contentType?: string
  filterSet?: string
  filter?: string
  redirectType?: string
  url?: string
  absolute?: boolean
  path?: string
  meta?: Record<string, unknown> | null
}

interface PathInfoParams {
  filterSet?: string
  language?: string | string[]
}

/**
 * Map GraphQL pathInfo to the REST pathinfo body shape getRouteInfo expects.
 */
export function mapGraphQLPathInfoToRest (pathInfo: PathInfoGraphqlPayload | null | undefined) {
  if (!pathInfo) {
    return { success: false }
  }
  let { type } = pathInfo
  if (type === 'unknown') {
    type = undefined
  }
  if (type === 'redirect') {
    return {
      success: pathInfo.success,
      type: 'redirect',
      redirectType: pathInfo.redirectType,
      url: pathInfo.url,
      absolute: pathInfo.absolute,
    }
  }
  return {
    success: pathInfo.success,
    type,
    id: pathInfo.id,
    vocabularyId: pathInfo.vocabularyId,
    vocabulary: pathInfo.vocabulary,
    content_type: pathInfo.contentType || null,
    filterSet: pathInfo.filterSet || null,
    filter: pathInfo.filter || null,
    redirectType: pathInfo.redirectType || null,
    url: pathInfo.url || null,
    meta: pathInfo.meta || null,
  }
}

/**
 * pathInfo is defined on Brooklyn GraphQL only — not on servers.graphql
 * (graphql.gaia.com / gaia-graphql MCP content schema).
 */
export function getPathInfoGraphqlUrl () {
  const config = getConfig()
  const isBrowser = Boolean(
    process.env.BROWSER || (global as { BROWSER_TEST?: boolean }).BROWSER_TEST,
  )
  const brooklyn = isBrowser
    ? _get(config, 'servers.brooklyn', '')
    : (_get(config, 'servers.brooklynInternal', '')
      || _get(config, 'servers.brooklyn', ''))
  return `${String(brooklyn).replace(/\/$/, '')}/graphql`
}

function isValidPathInfoPath (path: unknown): path is string {
  if (typeof path !== 'string') {
    return false
  }
  const trimmed = path.trim()
  if (!trimmed || trimmed === 'null' || trimmed === 'undefined') {
    return false
  }
  if (trimmed.startsWith('.well-known/') || trimmed.includes('%3F') || trimmed.includes('%3f')) {
    return false
  }
  return true
}

/**
 * Fetch pathinfo via Brooklyn GraphQL only (no REST pathinfo fallback).
 */
export async function fetchPathInfo ({
  path,
  pathInfoParams = {},
  auth,
}: {
  path: string
  pathInfoParams?: PathInfoParams
  auth?: unknown
}) {
  if (!isValidPathInfoPath(path)) {
    return { success: false }
  }
  const variables = {
    path,
    filterSet: pathInfoParams.filterSet || undefined,
    language: pathInfoParams.language || undefined,
  }
  try {
    const res = await apiPost(
      getPathInfoGraphqlUrl(),
      { query: PATH_INFO_QUERY, variables },
      {
        auth: createAuthForRequest({ auth: auth as never, includeJwt: false }),
        timeout: 10000,
        maxAttempts: 1,
      } as never,
      TYPE_GRAPHQL,
    )
    const errors = _get(res, ['body', 'errors'], [])
    if (errors.length) {
      logError(
        new Error(_get(errors, [0, 'message'], 'GraphQL pathInfo failed')),
        `GraphQL pathInfo failed for ${path}`,
      )
      return { success: false }
    }
    const pathInfo = _get(res, ['body', 'data', 'pathInfo']) as PathInfoGraphqlPayload | null
    return mapGraphQLPathInfoToRest(pathInfo)
  } catch (e) {
    logError(e instanceof Error ? e : new Error(String(e)), `GraphQL pathInfo failed for ${path}`)
    return { success: false }
  }
}
